import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { PortalShell } from "@/components/AppHeader";
import { MapView } from "@/components/MapView";
import { RatingForm, CommentForm, ComplaintForm, Stars } from "@/components/FeedbackForms";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const LegMap = ({ title, leg, testId, pickupLeg }) => {
  const start = leg.start && { ...leg.start, address: leg.start.address || "Driver start" };
  const map = pickupLeg
    ? <MapView className="h-[240px] sm:h-[300px]" pickup={leg.end} stops={[]} driver={start} route={leg.path} testId={`${testId}-map`} />
    : <MapView className="h-[240px] sm:h-[300px]" pickup={start} stops={[leg.end]} route={leg.path} testId={`${testId}-map`} />;
  return (
    <div className="lux-card min-w-0 space-y-3 p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-lg text-amber-50">{title}</p>
        <p className="text-xs text-zinc-400" data-testid={`${testId}-stats`}>{leg.miles != null ? `${leg.miles} mi` : "—"} · {leg.minutes != null ? `${leg.minutes} min` : "—"}</p>
      </div>
      {start && leg.end ? map : <p className="py-10 text-center text-sm text-zinc-500">No route for this leg.</p>}
      {leg.note && <p data-testid={`${testId}-note`} className="text-xs text-amber-500">{leg.note}{start && leg.end ? " — showing the planned route" : ""}</p>}
      {pickupLeg && start && <p className="text-[11px] text-zinc-500">Car = where the driver accepted · P = pickup</p>}
      <p className="text-xs text-zinc-500">{leg.started_at ? fmtDate(leg.started_at) : "—"} → {leg.ended_at ? fmtDate(leg.ended_at) : "—"}</p>
    </div>
  );
};

const AdminRatings = ({ ratings }) => (
  <div className="lux-card grid gap-4 p-5 sm:grid-cols-2 sm:p-6" data-testid="admin-trip-ratings">
    {[["customer", "Customer → driver"], ["driver", "Driver → customer"]].map(([k, l]) => (
      <div key={k}><p className="lux-eyebrow mb-2">{l}</p>{ratings[k] ? <><Stars value={ratings[k].stars} size="h-5 w-5" testId={`admin-rating-${k}`} />{ratings[k].quality && <p className="mt-1 text-xs text-zinc-400">Quality {ratings[k].quality}/5</p>}</> : <p className="text-sm text-zinc-500">Not rated</p>}</div>
    ))}
  </div>
);

export default function TripFeedbackPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [fb, setFb] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/trips/${id}/feedback`).then(({ data }) => setFb(data)).catch((e) => setError({ code: e.response?.status, msg: errMsg(e) })), [id]);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalShell>
      <Link to={`/trips/${id}`} data-testid="feedback-back" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-200"><ArrowLeft className="h-4 w-4" /> Trip details</Link>
      {error && (
        <div data-testid="feedback-error" className="lux-card mx-auto max-w-md p-10 text-center">
          <Lock className="mx-auto h-8 w-8 text-amber-400" />
          <p className="mt-4 font-serif text-2xl text-amber-50">{error.code === 403 ? "Access denied" : "Trip not found"}</p>
          <p className="mt-2 text-sm text-zinc-400">{error.msg}</p>
        </div>
      )}
      {fb && (
        <div className="space-y-6" data-testid="trip-feedback-page">
          <div>
            <p className="lux-eyebrow">Trip feedback</p>
            <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">{fb.pickup.address.split(",")[0]} → {fb.stops.at(-1)?.address.split(",")[0]}</h1>
            <p className="mt-2 text-sm text-zinc-400">{fb.completed_at ? fmtDate(fb.completed_at) : fb.status} · Driver {fb.driver_name || "—"} · Customer {fb.customer_name}</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <LegMap title="Pickup route" leg={fb.pickup_route} testId="pickup-route" pickupLeg />
            <LegMap title="Driving route" leg={fb.driving_route} testId="driving-route" />
          </div>
          {user.role === "admin" && <AdminRatings ratings={fb.ratings} />}
          {fb.can_give_feedback && (
            <div className="grid gap-6 lg:grid-cols-2">
              <RatingForm key={fb.my_rating?.updated_at || "new"} fb={fb} role={user.role} onDone={load} />
              <CommentForm fb={fb} onDone={load} />
            </div>
          )}
          {fb.can_complain && <ComplaintForm fb={fb} onDone={load} />}
          {user.role === "admin" && (
            <div className="lux-card p-5 sm:p-6" data-testid="admin-trip-comments">
              <p className="lux-eyebrow mb-3">Comments & complaints on this trip</p>
              {[...fb.comments, ...fb.complaints].length === 0 && <p className="text-sm text-zinc-500">None.</p>}
              {fb.comments.map((c) => <p key={c.id} className="border-t border-white/5 py-2 text-sm text-zinc-300 first:border-0">“{c.text}” <span className="text-xs text-zinc-500">· {c.author_name} ({c.author_role})</span></p>)}
              {fb.complaints.map((c) => <p key={c.id} className="border-t border-white/5 py-2 text-sm text-zinc-300 first:border-0"><span className="text-amber-200">{c.category}</span> · {c.status} · {c.description} <span className="text-xs text-zinc-500">· {c.author_name} ({c.author_role}){c.admin_note ? ` · note: ${c.admin_note}` : ""}</span></p>)}
            </div>
          )}
          {!fb.can_give_feedback && user.role !== "admin" && <p className="text-sm text-zinc-500">Ratings and comments open once the trip is completed.</p>}
        </div>
      )}
    </PortalShell>
  );
}
